import { featureEach } from '@turf/meta';
import unkinkPolygon from '@turf/unkink-polygon';
import cx from 'classnames';
import { makeUnkinkable } from "../../../core/makeUnkinkable";
import without from 'lodash/without';
import { Suspense, createContext, lazy, useContext, useState } from 'react';
import { Label, Text, tombac } from 'tombac';
import { AddPolygonIcon, CancelIcon } from 'tombac-icons';
import "./GeoLimitEditor.css";
import { GeoLimitImportExport } from "./GeoLimitImportExport";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GeoLimitEditorMap = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "geo-limit-editor" */"./GeoLimitEditorMap"));
export const GeoLimitEditorContext = /*#__PURE__*/createContext(null);
export function validateCrossedMeridian(geometry) {
  const coords = geometry?.coordinates;
  const lngOnly = coords[0].reduce((acc, current) => {
    return acc.concat(360 - Math.abs(current[0]));
  }, []);
  const checkAllLower = lngOnly.every(el => {
    return el < 180;
  });
  const checkAllGreater = lngOnly.every(el => {
    return el > 180;
  });
  return checkAllLower === checkAllGreater;
}
export function initLoadedGeoLimit(limit) {
  if (!limit) return limit;
  let featureNum = 1;
  const featureCollection = limit.type === 'FeatureCollection' ? limit : unkinkPolygon(makeUnkinkable(limit));
  featureEach(featureCollection, feature => {
    const isCrossedMeridian = validateCrossedMeridian(feature.geometry);
    feature.id = feature.id ?? featureNum;
    feature.properties = {
      ...feature.properties,
      featureName: feature.properties.name ?? `Region ${featureNum}`,
      isCrossedMeridian: isCrossedMeridian
    };
    featureNum++;
  });
  return featureCollection;
}
export default function GeoLimitEditor({
  className,
  label,
  limit,
  onChange,
  schema
}) {
  const {
    drawControl
  } = useContext(GeoLimitEditorContext);
  const selected = drawControl ? drawControl.selectedFeatureIds : [];
  return /*#__PURE__*/_jsxs("div", {
    className: cx('GeoLimitEditor', className),
    "data-sentry-component": "GeoLimitEditor",
    "data-sentry-source-file": "GeoLimitEditor.tsx",
    children: [/*#__PURE__*/_jsxs("div", {
      className: "GeoLimitEditor__header",
      children: [/*#__PURE__*/_jsx(Label, {
        $flex: "auto",
        "data-sentry-element": "Label",
        "data-sentry-source-file": "GeoLimitEditor.tsx",
        "data-sentry-element": "Label",
        children: label
      }), limit?.features?.length > 0 && /*#__PURE__*/_jsx(Text, {
        $color: tombac.color('danger'),
        $mt: "0.1em",
        as: "button",
        className: "GeoLimitEditor__delete-all",
        onClick: () => onChange(null),
        variant: "interactive",
        children: "Delete all"
      })]
    }), limit?.features?.length ? /*#__PURE__*/_jsx("ul", {
      className: "GeoLimitEditor__region-list",
      children: limit.features.map(feature => {
        const isCrossedMeridian = feature.properties.isCrossedMeridian;
        function selectRegion() {
          drawControl.preventDeselect();
          drawControl.setSelectedFeatureIds([feature.id]);
          drawControl.setMode(drawControl.draw.modes.SIMPLE_SELECT);
        }
        return /*#__PURE__*/_jsxs("li", {
          className: cx('GeoLimitEditor__region', isCrossedMeridian && 'GeoLimitEditor__region--invalid', selected.find(id => id === feature.id) && 'GeoLimitEditor__region--selected'),
          onClick: selectRegion,
          onKeyUp: event => event.key === 'Enter' && selectRegion(),
          onMouseEnter: () => {
            feature.properties.highlighted = true;
            onChange({
              ...limit
            });
          },
          onMouseLeave: () => {
            feature.properties.highlighted = false;
            onChange({
              ...limit
            });
          },
          tabIndex: 0,
          children: [feature.properties.featureName, ' ', isCrossedMeridian && '- Invalid, region is crossing 180th meridian', /*#__PURE__*/_jsx("button", {
            className: "GeoLimitEditor__remove-region",
            onClick: () => onChange({
              ...limit,
              features: without(limit.features, feature)
            }),
            children: /*#__PURE__*/_jsx(CancelIcon, {})
          })]
        }, feature.id);
      })
    }) : /*#__PURE__*/_jsxs("p", {
      className: "GeoLimitEditor__description",
      children: ["Restrict to selected regions. Draw them using ", /*#__PURE__*/_jsx(AddPolygonIcon, {}), ' ', "polygon tool."]
    }), /*#__PURE__*/_jsx(GeoLimitImportExport, {
      limit: limit,
      onChange: onChange,
      productId: 'productId' in schema ? schema.productId : '',
      "data-sentry-element": "GeoLimitImportExport",
      "data-sentry-source-file": "GeoLimitEditor.tsx",
      "data-sentry-element": "GeoLimitImportExport"
    })]
  });
}
GeoLimitEditor.ExtendedEditor = function GeoLimitEditorExtendedEditor({
  className,
  ...props
}) {
  return /*#__PURE__*/_jsx("div", {
    className: cx('GeoLimitEditor', className),
    children: /*#__PURE__*/_jsx(Suspense, {
      fallback: /*#__PURE__*/_jsx("div", {
        className: "GeoLimitEditor__map"
      }),
      children: /*#__PURE__*/_jsx(GeoLimitEditorMap, {
        ...props
      })
    })
  });
};
GeoLimitEditor.ContextProvider = function GeoLimitEditorContextProvider({
  children
}) {
  const [drawControl, setDrawControl] = useState(null);
  return /*#__PURE__*/_jsx(GeoLimitEditorContext.Provider, {
    value: {
      drawControl,
      setDrawControl
    },
    children: children
  });
};